/*
 * Gift box offer. Where it renders, and when it hides, is documented in
 * snippets/gift-box-offer.liquid.
 *
 * Progressive enhancement over a plain <form action="/cart/add">. Anything this
 * script can't handle is left to the native post, which adds the box and
 * redirects to the cart page. That covers a cart page or popup this script
 * doesn't recognise, such as the cart drawer if cart_type is ever switched.
 *
 * Two hosts, each refreshed with the sections it already declares in
 * getSectionsToRender(), so the offer follows any future change there:
 *
 * - <cart-notification> (the add-to-cart popup): renders the new sections in
 *   place. It deliberately does not call renderContents(), because that calls
 *   open() on a popup that is already open. The transitionend listener open()
 *   leaves behind would then fire on close and trap focus inside the hidden
 *   popup.
 *
 * - <cart-items> (the cart page): renders every section except those inside
 *   <cart-items>. Those are refreshed by CartItems#onCartUpdate when
 *   cart-update is published below, and rendering them here too would do it
 *   twice.
 */
if (!customElements.get('gift-box-offer')) {
  customElements.define(
    'gift-box-offer',
    class GiftBoxOffer extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('submit', this.onSubmit.bind(this));
      }

      onSubmit(event) {
        const notification = this.closest('cart-notification');
        const cartItems = notification ? null : document.querySelector('cart-items');
        const host = notification || cartItems;
        if (typeof host?.getSectionsToRender !== 'function') return;

        event.preventDefault();

        const form = event.target;
        const button = form.querySelector('[type="submit"]');
        if (button.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(button, true);
        this.showError('');

        const variantId = form.querySelector('[name="id"]').value;
        const sections = notification
          ? host.getSectionsToRender()
          : host.getSectionsToRender().filter((section) => !host.contains(document.getElementById(section.id)));

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: sections.map((section) => section.section || section.id),
          sections_url: window.location.pathname,
        });

        const linesUpdate = this.createCartLinesUpdateEvent(variantId, notification ? 'dialog' : 'cart');

        fetch(routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const message = response.description || response.message || window.cartStrings?.error;
              this.showError(message);
              this.setLoading(button, false);
              this.dispatchCartErrorEvent(message, 'INVALID');
              linesUpdate?.reject(new Error(message));
              return;
            }

            this.resolveCartLinesUpdate(linesUpdate);

            if (notification) {
              this.renderNotification(notification, response);
            } else {
              this.renderCartPage(sections, response);
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'gift-box-offer',
              productVariantId: variantId,
              cartData: response,
            });
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings?.error);
            this.setLoading(button, false);
            this.dispatchCartErrorEvent(error.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdate?.reject(error);
          });
      }

      // Same loop as CartNotification#renderContents, without the open() call
      // (see the header comment). The popup now shows the gift box as the item
      // just added, and the offer is gone because the box is in the cart.
      renderNotification(notification, response) {
        notification.cartItemKey = response.key;
        notification.getSectionsToRender().forEach((section) => {
          const mount = document.getElementById(section.id);
          const html = response.sections?.[section.id];
          if (!mount || !html) return;
          mount.innerHTML = notification.getSectionInnerHTML(html, section.selector);
        });

        // The button that had focus was just replaced. Re-trap focus on the
        // new contents, which also moves focus to the dialog itself.
        trapFocus(notification.notification);
      }

      // Replaces this element too: the footer comes back with the offer in its
      // "added" state. Focus follows to that message so it gets read out.
      renderCartPage(sections, response) {
        sections.forEach((section) => {
          const root = document.getElementById(section.id);
          const html = response.sections?.[section.section];
          if (!root || !html) return;
          const target = root.querySelector(section.selector) || root;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (source) target.innerHTML = source.innerHTML;
        });

        document.querySelector('.gift-box-offer--cart .gift-box-offer__status')?.focus();
      }

      setLoading(button, loading) {
        button.classList.toggle('loading', loading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        if (loading) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      }

      showError(message) {
        const error = this.querySelector('.gift-box-offer__error');
        if (!error) return;
        error.textContent = message || '';
        error.hidden = !message;
      }

      // Shopify storefront standard events, mirroring assets/product-form.js so
      // the box counts as an add to cart like any other product.
      createCartLinesUpdateEvent(variantId, context) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context,
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!deferred || !CartLinesUpdateEvent) return;

        fetch(`${routes.cart_url}.json`)
          .then((response) => response.json())
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }
    }
  );
}
